// Suspension handling in the main app (backend/docs/admin.md → Suspended users /
// Suspended workspaces). Pure helpers plus a tiny store, so they are unit tested.
import { useSyncExternalStore } from "react";

import { ApiError, errorMessage } from "./api";
import { SUSPENDED_ACCOUNT_MESSAGE } from "./sso";

export const ACCOUNT_SUSPENDED = "ACCOUNT_SUSPENDED";
export const WORKSPACE_SUSPENDED = "WORKSPACE_SUSPENDED";

export function isAccountSuspended(err: unknown): boolean {
  return err instanceof ApiError && err.code === ACCOUNT_SUSPENDED;
}

/** An org-scoped request refused because the workspace is suspended (403 WORKSPACE_SUSPENDED). */
export function isWorkspaceSuspended(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403 && err.code === WORKSPACE_SUSPENDED;
}

/** The message under the sign-in form: a fixed text for suspended accounts, else the server's. */
export function loginErrorMessage(err: unknown): string {
  return isAccountSuspended(err) ? SUSPENDED_ACCOUNT_MESSAGE : errorMessage(err);
}

/**
 * The workspace a failed request was about. Org-scoped query keys start with
 * ["org", orgId]; mutations have no key, so the current URL (/o/:orgId) is used.
 */
export function suspendedOrgId(queryKey: readonly unknown[] | undefined, pathname: string): string | null {
  if (queryKey && queryKey[0] === "org" && typeof queryKey[1] === "string" && queryKey[1]) return queryKey[1];

  return /^\/o\/([^/]+)/.exec(pathname)?.[1] ?? null;
}

// ---- store -------------------------------------------------------------------------
// Module state on purpose: one set of suspended workspace ids per page, written
// from the query cache (outside React) and read by the app shell.

let suspended: ReadonlySet<string> = new Set();
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export function markWorkspaceSuspended(orgId: string) {
  if (suspended.has(orgId)) return;
  suspended = new Set([...suspended, orgId]);
  emit();
}

export function clearWorkspaceSuspended(orgId: string) {
  if (!suspended.has(orgId)) return;
  const next = new Set(suspended);
  next.delete(orgId);
  suspended = next;
  emit();
}

export function isWorkspaceMarkedSuspended(orgId: string) {
  return suspended.has(orgId);
}

/** Resets the store (sign-out, tests). */
export function resetSuspensions() {
  suspended = new Set();
  emit();
}

function subscribe(listener: () => void) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/** True once any request of this workspace was refused as suspended. */
export function useWorkspaceSuspended(orgId: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => suspended.has(orgId),
    () => false,
  );
}

/** Records a WORKSPACE_SUSPENDED error; returns whether it was one. */
export function noteSuspension(err: unknown, queryKey: readonly unknown[] | undefined, pathname: string) {
  if (!isWorkspaceSuspended(err)) return false;
  const orgId = suspendedOrgId(queryKey, pathname);
  if (orgId) markWorkspaceSuspended(orgId);

  return true;
}
